import apiClient from './api'

function normalizeError(error, fallbackMessage) {
  const message = error?.response?.data?.message
    || error?.message
    || fallbackMessage
  return new Error(String(message))
}

function createEmptyReceiptSummary() {
  return {
    totalCount: 0,
    monthlyCount: 0,
    average: 0,
    currency: 'EUR',
  }
}

function createEmptyRecognitionSummary() {
  return {
    total: 0,
    byStatus: {
      pending: 0,
      processing: 0,
      completed: 0,
      failed: 0,
    },
    bySource: {
      image: 0,
      qr: 0,
      speech: 0,
    },
    byEndpointGroup: {
      public_recognition: 0,
      telegram_mini_app_recognition: 0,
      unknown: 0,
    },
    successCount: 0,
    failureCount: 0,
    publicRecognitionCount: 0,
    telegramRecognitionCount: 0,
    averageDurationMs: null,
    latestUpdatedAt: null,
    latestCompletedAt: null,
    latestFailedAt: null,
    successRate: null,
  }
}

export default {
  async getRealtimeDashboardSnapshot() {
    try {
      const res = await apiClient.get('/admin/dashboard/realtime')
      return res.data?.data || res.data
    } catch (error) {
      throw normalizeError(error, 'Failed to load realtime dashboard snapshot')
    }
  },
  status() {
    return apiClient.get('/queue/admin/queue-workers').then((res) => res.data?.data || res.data)
  },
  setWorkersEnabled(enabled) {
    return apiClient.put('/queue/admin/queue-workers/enabled', { enabled })
      .then((res) => res.data?.data || res.data)
  },
  setQueueEnabled(queueKey, enabled) {
    return apiClient.put(`/queue/admin/queue-workers/${queueKey}/enabled`, { enabled })
      .then((res) => res.data?.data || res.data)
  },
  // Returns aggregate stats for all queues from the admin API
  adminQueues() {
    return apiClient.get('/queue/admin/queues')
  },
  // Returns paginated cross-queue jobs list for dashboard polling.
  adminJobs(params = {}) {
    return apiClient.get('/queue/admin/jobs', { params })
  },
  // Returns aggregate admin metrics (with fallback to queue metrics endpoint).
  async adminMetrics() {
    try {
      const res = await apiClient.get('/admin/metrics')
      return res.data?.data || res.data
    } catch {
      const snapshot = await this.getRealtimeDashboardSnapshot()
      const queues = Array.isArray(snapshot?.queues) ? snapshot.queues : []
      const totalIfKnown = (field) => (
        queues.length === 0 || queues.some((queue) => typeof queue?.[field] !== 'number')
          ? null
          : queues.reduce((sum, queue) => sum + queue[field], 0)
      )
      const summary = {
        pending: totalIfKnown('waitingCount'),
        processing: totalIfKnown('activeCount'),
        completed: totalIfKnown('completedCount'),
        failed: totalIfKnown('failedCount'),
      }

      return {
        generatedAt: snapshot?.generatedAt || new Date().toISOString(),
        systemHealth: snapshot?.systemHealth || 'idle',
        queues,
        summary,
        receiptSummary: snapshot?.dataSummary
          ? {
              totalCount: Number(snapshot.dataSummary.totalReceipts || 0),
              monthlyCount: Number(snapshot.dataSummary.receiptsThisMonth || 0),
              average: Number(snapshot.dataSummary.averageReceiptAmount || 0),
              currency: 'EUR',
            }
          : createEmptyReceiptSummary(),
        recognitionSummary: createEmptyRecognitionSummary(),
        pipelines: Array.isArray(snapshot?.pipelines) ? snapshot.pipelines : [],
        recentJobs: Array.isArray(snapshot?.recentJobs) ? snapshot.recentJobs : [],
        lastErrors: Array.isArray(snapshot?.lastErrors) ? snapshot.lastErrors : [],
        activeAlerts: Array.isArray(snapshot?.activeAlerts) ? snapshot.activeAlerts : [],
        dataSummary: snapshot?.dataSummary || {
          totalReceipts: 0,
          receiptsThisMonth: 0,
          completedProcessingCount: 0,
          failedProcessingCount: 0,
          averageReceiptAmount: 0,
        },
        warnings: ['admin_metrics_fallback'],
      }
    }
  },
  // Poll alerts feed for toast/banner UI.
  adminAlerts(params = {}) {
    return apiClient.get('/admin/alerts', { params })
      .then((res) => res.data?.data || [])
  },
  // Acknowledge and remove an alert from active feed.
  acknowledgeAlert(id) {
    return apiClient.post(`/admin/alerts/${id}/ack`)
      .then((res) => res.data?.data || res.data)
  },
  // Backward compatibility alias.
  ackAlert(id) {
    return this.acknowledgeAlert(id)
  },
  // Resolution endpoint is optional; keep method explicit for future backend support.
  async resolveAlert(id) {
    try {
      const res = await apiClient.post(`/admin/alerts/${id}/resolve`)
      return res.data?.data || res.data
    } catch (error) {
      // Gracefully degrade when backend does not expose dedicated resolve yet.
      if (error?.response?.status === 404) {
        return this.acknowledgeAlert(id)
      }
      throw normalizeError(error, 'Failed to resolve alert')
    }
  },
  async retryFailedJobs(ids = []) {
    const safeIds = Array.isArray(ids) ? ids.filter(Boolean) : []
    try {
      const results = await Promise.allSettled(
        safeIds.map((id) => apiClient.post(`/queue/admin/jobs/${id}/retry`)),
      )
      return {
        total: safeIds.length,
        succeeded: results.filter((row) => row.status === 'fulfilled').length,
        failed: results.filter((row) => row.status === 'rejected').length,
      }
    } catch (error) {
      throw normalizeError(error, 'Failed to retry failed jobs')
    }
  },
  async getRecentJobs(limit = 20) {
    const snapshot = await this.getRealtimeDashboardSnapshot()
    const rows = Array.isArray(snapshot?.recentJobs) ? snapshot.recentJobs : []
    return rows.slice(0, Math.max(1, Number(limit) || 20))
  },
  async getRecentErrors(limit = 10) {
    const snapshot = await this.getRealtimeDashboardSnapshot()
    const rows = Array.isArray(snapshot?.lastErrors) ? snapshot.lastErrors : []
    return rows.slice(0, Math.max(1, Number(limit) || 10))
  },
  // Returns a single job by ID
  getJob(id) {
    return apiClient.get(`/queue/admin/jobs/${id}`)
  },
  // Returns step logs for a job
  getJobLogs(id) {
    return apiClient.get(`/queue/admin/jobs/${id}/logs`)
  },
  // Returns pipeline trace steps for a job
  getJobPipeline(id) {
    return apiClient.get(`/queue/admin/jobs/${id}/pipeline`)
  },
  // Replay a job with original data
  replayJob(id) {
    return apiClient.post(`/queue/admin/jobs/${id}/replay`)
  },
  // Retry a job (reset to pending)
  retryJob(id) {
    return apiClient.post(`/queue/admin/jobs/${id}/retry`)
  },
  // Remove a job
  removeJob(id) {
    return apiClient.post(`/queue/admin/jobs/${id}/remove`)
  },
}

